/**
 * What changed about each module between two approved revisions.
 *
 * @module components/portfolio/comparison/module-differences
 */

import { humanizeIdentifier } from "@/components/shared/diff-labels";
import type { RevisionFieldChange } from "@/server/governance-diff";
import type { Module, Programme } from "@/types";
import { stableJson } from "@/utils/stable-json";
import {
  placementName,
  programmeTeachingPeriodLabel,
  teachingPeriodName,
} from "@/utils/teaching-period";

import type { ModuleDifference, ModuleDifferenceStatus } from "./diff-types";

function authoredModule(module: Module): Omit<Module, "stage" | "semester" | "isElective"> {
  const { stage: _stage, semester: _semester, isElective: _isElective, ...content } = module;
  return content;
}

function modulePlacements(programme: Programme): Map<string, string[]> {
  const placements = new Map<string, Set<string>>();
  const add = (moduleId: string, placement: string): void => {
    const current = placements.get(moduleId) ?? new Set<string>();
    current.add(placement);
    placements.set(moduleId, current);
  };
  const electives = new Map(
    (programme.electiveDefinitions ?? []).map((definition) => [definition.id, definition]),
  );

  const periodLabel = programmeTeachingPeriodLabel(programme);
  (programme.versions ?? []).forEach((version) => {
    (version.stages ?? []).forEach((stage) => {
      const stageLabel =
        stage.name ?? `Stage ${stage.academicStage ?? stage.sequence ?? "unlabelled"}`;
      const prefix = `${version.label}: ${stageLabel}`;
      (stage.modules ?? []).forEach(({ moduleId, semester }) =>
        add(moduleId, `${version.label}: ${placementName(stageLabel, semester, periodLabel)}`),
      );
      (stage.moduleIds ?? []).forEach((moduleId) => add(moduleId, prefix));
      (stage.electiveChoiceSlots ?? []).forEach((slot) => {
        const definition = electives.get(slot.electiveDefinitionId);
        definition?.groups.forEach((group) =>
          group.moduleIds.forEach((moduleId) =>
            add(
              moduleId,
              `${prefix}, ${definition.name} / ${group.name}${slot.semester ? `, ${teachingPeriodName(slot.semester, periodLabel)}` : ""}`,
            ),
          ),
        );
      });
    });
  });

  (programme.modules ?? []).forEach((module) => {
    if (
      !placements.has(module.id) &&
      (module.stage !== undefined || module.semester !== undefined)
    ) {
      add(
        module.id,
        [
          module.stage === undefined ? undefined : `Stage ${module.stage}`,
          module.semester === undefined ? undefined : `Semester ${module.semester}`,
        ]
          .filter((value): value is string => value !== undefined)
          .join(", "),
      );
    }
  });

  return new Map(
    [...placements].map(([moduleId, values]) => [
      moduleId,
      [...values].sort((a, b) => a.localeCompare(b)),
    ]),
  );
}

function changedSections(moduleId: string, changes: RevisionFieldChange[]): string[] {
  const prefix = `modules[${moduleId}]`;
  const sections = new Set<string>();
  changes
    .filter((change) => change.path.startsWith(prefix))
    .forEach((change) => {
      const path = change.path.slice(prefix.length + 1);
      const root = path.split(/[.[]/, 1)[0];
      if (root) {
        const labels: Record<string, string> = {
          mimlos: "MIMLOs",
          readingList: "Reading list",
          effortHours: "Effort hours",
          coRequisites: "Co-requisites",
          indicativeContent: "Indicative content",
          isElective: "Elective status",
          maximumLearners: "Maximum learners",
          moduleLeadEmail: "Module lead email",
          moduleLeadName: "Module lead name",
          resourcesRequired: "Resources required",
          staffQualifications: "Staff qualifications",
        };
        sections.add(labels[root] ?? humanizeIdentifier(root));
      }
    });
  return [...sections].sort((a, b) => a.localeCompare(b));
}

export function equivalentWeightingChange(
  change: RevisionFieldChange,
  changes: RevisionFieldChange[],
): boolean {
  if (!change.path.endsWith(".weight")) {
    return false;
  }
  const weightingPath = `${change.path.slice(0, -"weight".length)}weighting`;
  return changes.some(
    (candidate) =>
      candidate.path === weightingPath &&
      stableJson(candidate.before) === stableJson(change.before) &&
      stableJson(candidate.after) === stableJson(change.after),
  );
}

export function compareModules(
  before: Programme,
  after: Programme,
  changes: RevisionFieldChange[],
): ModuleDifference[] {
  const beforeById = new Map((before.modules ?? []).map((module) => [module.id, module]));
  const afterById = new Map((after.modules ?? []).map((module) => [module.id, module]));
  const beforePlacement = modulePlacements(before);
  const afterPlacement = modulePlacements(after);

  return [...new Set([...beforeById.keys(), ...afterById.keys()])]
    .map((id): ModuleDifference => {
      const left = beforeById.get(id);
      const right = afterById.get(id);
      const beforePlacements = beforePlacement.get(id) ?? [];
      const afterPlacements = afterPlacement.get(id) ?? [];
      const fieldChanges = changes.filter(
        (change) =>
          change.path.startsWith(`modules[${id}]`) && !equivalentWeightingChange(change, changes),
      );
      const statuses: ModuleDifferenceStatus[] = [];
      if (!left) {
        statuses.push("new");
      } else if (!right) {
        statuses.push("removed");
      } else {
        if (stableJson(authoredModule(left)) !== stableJson(authoredModule(right))) {
          statuses.push("edited");
        }
        if (stableJson(beforePlacements) !== stableJson(afterPlacements)) {
          statuses.push("moved");
        }
        if (statuses.length === 0) {
          statuses.push("unchanged");
        }
      }
      const display = right ?? left;
      return {
        id,
        code: display?.code ?? "No code",
        title: display?.title ?? "Untitled module",
        credits: display?.credits ?? 0,
        statuses,
        beforePlacements,
        afterPlacements,
        changedSections: changedSections(id, changes),
        fieldChanges,
        ...(left ? { before: left } : {}),
        ...(right ? { after: right } : {}),
      };
    })
    .sort((left, right) => {
      const leftUnchanged = left.statuses.includes("unchanged") ? 1 : 0;
      const rightUnchanged = right.statuses.includes("unchanged") ? 1 : 0;
      return leftUnchanged - rightUnchanged || left.code.localeCompare(right.code);
    });
}
